import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import type { Filters, Meta, Stats } from '../../src/api/queries';
import { DEFAULT_SOURCE, type Source } from '../../src/source';
import { fetchMeta, fetchStats, META_URL, statsUrl } from './api';
import { useSource } from './source-context';
import type { ViewState } from './useUrlState';

/**
 * The view state, as the API's filter block — and the headline figures for it.
 *
 * Every page opened with the same nine lines: build a `Filters` out of `ViewState`, key a query on
 * `statsUrl(filters)`, fetch. Four copies of an object literal is four chances to forget a field,
 * and the field that gets forgotten is always the newest one — the prefecture control shipped in the
 * commit before this, and a page whose badges quietly ignored it would report national numbers under
 * a prefecture heading. One function means a widened `ViewState` reaches every page at once.
 *
 * `viewFilters` is exported separately because the chart and the feed need the same block without
 * the stats query: they call `/api/series` and `/api/incidents` with it.
 */

export function viewFilters(
  category: string,
  state: ViewState,
  source: Source = DEFAULT_SOURCE,
): Filters {
  return {
    category,
    from: state.from,
    to: state.to,
    granularity: state.g,
    // The series is the page's identity, not the reader's choice, so it arrives from
    // `SourceProvider` rather than from `ViewState` — see `source-context.tsx` for why a URL must
    // not be able to reach it. The default keeps every pre-existing digest page working untouched.
    source,
    prefecture: state.prefecture,
    domesticOnly: state.domesticOnly,
    // The traffic page's two controls, threaded here rather than at each call site: this function
    // exists so that a widened `ViewState` reaches every panel at once, and a chip that narrowed
    // the badges while the accident line under them showed the whole corpus is the caption-against-
    // numbers failure the mandatory filter banner promises does not happen.
    roadUser: state.roadUser,
    county: state.county,
    // The one list-valued filter, and the reason above applies to it hardest: it is offered on all
    // five data pages, so a page that built its own literal would be a page whose feed disagrees
    // with its badges.
    municipalities: state.municipalities,
  };
}

/**
 * `/api/stats` for one category and one window.
 *
 * The query key is the request URL, exactly as everywhere else in this app: it is derived from the
 * same filters the request carries, so a key and a response can never describe different windows.
 * `statsUrl` deliberately omits `granularity` (`api.ts`), so switching bucket size does not evict
 * these numbers — they do not depend on it.
 */
export function useStats(category: string, state: ViewState): UseQueryResult<Stats, Error> {
  const filters = viewFilters(category, state, useSource());
  return useQuery({
    queryKey: [statsUrl(filters)],
    queryFn: () => fetchStats(filters),
  });
}

/**
 * `/api/meta`, from the cache the header already filled — the coverage fact, for the pages.
 *
 * The components that draw charts fetch this themselves (see `TimeSeriesChart` on why it is not a
 * prop). The *pages* need it too, because a badge's note and a caption can each make the same claim
 * a chart's zero-note makes, and a page that has to remember to thread `meta` down is a page that
 * will one day forget and silently lose the caveat. One hook, one key, no second request.
 */
export function useMeta(): UseQueryResult<Meta, Error> {
  return useQuery({ queryKey: [META_URL], queryFn: fetchMeta });
}
